import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SkeletonText } from '../components/ui/Skeleton';

/** Formulário público de captação (/f/:id): nome, WhatsApp e consentimento */
export function CaptureFormPage() {
  const { id } = useParams();
  const [info, setInfo] = useState<{ company: string; name: string; consentText: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`/api/public/captacao/${id}`)
      .then(async (r) => (r.ok ? setInfo(await r.json()) : setError('Este formulário não existe ou foi encerrado.')))
      .catch(() => setError('Não foi possível carregar o formulário.'));
  }, [id]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/public/captacao/${id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome, telefone, consent, website }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.message || 'Não foi possível enviar');
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível enviar');
    } finally {
      setBusy(false);
    }
  };

  const input = { width: '100%', height: 44, border: '1px solid var(--border-strong)', borderRadius: 8, padding: '0 12px', fontSize: 16 } as const;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, boxShadow: 'var(--shadow-card)' }}>
        {!info && !error && <SkeletonText lines={4} label="Carregando formulário" />}
        {!info && error && <p style={{ color: 'var(--crm-text-error)' }}>{error}</p>}
        {info && done && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40 }} aria-hidden>✅</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: '8px 0' }}>Pronto!</h1>
            <p style={{ color: 'var(--text-2)' }}>Você vai receber uma mensagem de {info.company} no seu WhatsApp. Para sair a qualquer momento, é só responder SAIR.</p>
          </div>
        )}
        {info && !done && (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <p style={{ fontSize: 13, color: 'var(--brand-text)', fontWeight: 600, margin: 0 }}>{info.company}</p>
              <h1 style={{ fontSize: 22, fontWeight: 600, margin: '4px 0 0' }}>Receba nossas novidades no WhatsApp</h1>
            </div>
            <label style={{ fontSize: 14, fontWeight: 500 }}>
              Seu nome
              <input style={{ ...input, marginTop: 4 }} value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" required minLength={2} maxLength={80} />
            </label>
            <label style={{ fontSize: 14, fontWeight: 500 }}>
              WhatsApp com DDD
              <input style={{ ...input, marginTop: 4 }} value={telefone} onChange={(e) => setTelefone(e.target.value)} inputMode="tel" autoComplete="tel" placeholder="(63) 99999-0000" required />
            </label>
            {/* campo invisível contra robôs */}
            <input aria-hidden tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} />
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: 'var(--text-2)' }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} required />
              <span>{info.consentText}</span>
            </label>
            {error && <p style={{ color: 'var(--crm-text-error)', fontSize: 14, margin: 0 }} role="alert">{error}</p>}
            <button type="submit" disabled={busy || !consent}
              style={{ height: 46, borderRadius: 8, border: 0, background: consent ? 'var(--brand-text)' : 'var(--border-strong)', color: 'var(--crm-on-accent)', fontSize: 16, fontWeight: 600, cursor: consent ? 'pointer' : 'not-allowed' }}>
              {busy ? 'Enviando…' : 'Quero receber'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
